/* import __COLOCATED_TEMPLATE__ from './team-configuration.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */

import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type Store from '@ember-data/store';
import type Team from 'embercom/models/team';
import type AppService from 'embercom/services/app-service';

interface Arguments {
  onPrimaryTeamsChange: (primaryTeams: number[]) => void;
  onSecondaryTeamsChange: (secondaryTeams: number[]) => void;
  selectedPrimaryTeamIds: number[];
  selectedSecondaryTeamIds: number[];
}

interface Signature {
  Args: Arguments;
  Element: HTMLDivElement;
}

export default class TeamConfiguration extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: AppService;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    this.fetchTeamSettings();
  }

  async fetchTeamSettings(): Promise<void> {
    await this.store.findAll('team-setting');
  }

  get validSecondaryTeamIdsSet(): Set<string> {
    let nonLBTeamIds = this.store
      .peekAll('team-setting')
      .filter((teamSetting: any) => teamSetting.distributionMethod === 'load_balanced')
      .map((teamSetting: any) => teamSetting.teamId);

    return new Set(nonLBTeamIds);
  }

  get allAvailableTeams(): Team[] {
    return this.appService.app.teams.filter((team: Team) => !team.is_redacted);
  }

  get selectedPrimaryTeamIdsSet(): Set<number> {
    return new Set(this.args.selectedPrimaryTeamIds);
  }

  get selectedSecondaryTeamIdsSet(): Set<number> {
    return new Set(this.args.selectedSecondaryTeamIds);
  }

  get eligiblePrimaryTeamSelectItems(): any[] {
    return this.allAvailableTeams.map((team) => {
      let teamId = parseInt(team.id, 10);
      return {
        text: team.name,
        value: teamId,
        isSelected: this.selectedPrimaryTeamIdsSet.has(teamId),
        isDisabled: this.selectedSecondaryTeamIdsSet.has(teamId),
        component: 'settings/teammates/invites/configuration/team-select-item',
        componentAttrs: {
          team,
        },
      };
    });
  }

  get eligibleSecondaryTeamSelectItems(): any[] {
    let eligibleSecondaryTeams = this.allAvailableTeams.filter((team) =>
      this.validSecondaryTeamIdsSet.has(team.id),
    );
    return eligibleSecondaryTeams.map((team) => {
      let teamId = parseInt(team.id, 10);
      return {
        text: team.name,
        value: teamId,
        isSelected: this.selectedSecondaryTeamIdsSet.has(teamId),
        isDisabled: this.selectedPrimaryTeamIdsSet.has(teamId),
        component: 'settings/teammates/invites/configuration/team-select-item',
        componentAttrs: {
          team,
        },
      };
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teammates::Invites::Configuration::TeamConfiguration': typeof TeamConfiguration;
    'settings/teammates/invites/configuration/team-configuration': typeof TeamConfiguration;
  }
}
